/* eslint-disable no-await-in-loop */
import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { fetcher } from '@/utils/fetcher';
import { RETAIL_API_URL } from 'src/utils/envConfig';

import { Player } from '@/types/Configuration';
import { getWindowLogger } from '@/utils/remoteLogger';
import { LogAction, LogStatus } from '@/types/Logging';
import { useCurrentAuthSession } from '@/context/AuthContext';
import { ConfigMap, CreatedPlayersList } from '../AdminEditPage/AdminEditPage';
import { useConfigurations } from './useConfigurations';
import { useUnitData } from './useUnitData';
import { fsPath } from './__path';
import { useUserInfo } from './useUserInfo';

const logger = getWindowLogger({
  file: `${fsPath}/useUpdateConfigs.ts`,
});

const flatShelves = (data: ConfigMap) =>
  data?.flatMap((subsidiary) =>
    subsidiary.shelves.map((shelf) => ({
      ...shelf,
      subsidiaryId: subsidiary.id,
    })),
  );

const flatPlayers = (data: ConfigMap) =>
  flatShelves(data)?.flatMap((shelf) =>
    shelf.players.map((player: Player) => ({
      ...player,
      subsidiaryId: shelf?.subsidiaryId,
      shelfId: shelf?.id,
    })),
  );

export const getDiffConfigs = (oldData: ConfigMap, newData: ConfigMap) => {
  // subsidiaries

  const createdSubsidiaries =
    newData?.filter(
      (newItem) => !oldData?.some((oldItem) => oldItem.id === newItem.id),
    ) ?? [];

  const deletedSubsidiaries =
    oldData?.filter(
      (oldItem) => !newData?.some((newtem) => newtem.id === oldItem.id),
    ) ?? [];

  const updatedSubsidiaries =
    oldData
      ?.filter((oldElement) => {
        const updatedElement = newData?.find(
          (updatedItem) => updatedItem.id === oldElement.id,
        );
        return updatedElement && updatedElement.name !== oldElement.name;
      })
      .map((oldElement) =>
        newData?.find((updatedItem) => updatedItem.id === oldElement.id),
      ) ?? [];

  // shelves

  const createdShelves =
    flatShelves(newData)?.filter(
      (newItem) =>
        !flatShelves(oldData)?.some((oldItem) => oldItem.id === newItem.id),
    ) ?? [];

  const deletedShelves =
    flatShelves(oldData)?.filter(
      (oldItem) =>
        !flatShelves(newData)?.some((newtem) => newtem.id === oldItem.id),
    ) ?? [];

  const updatedShelves =
    flatShelves(oldData)
      ?.filter((oldElement) => {
        const updatedElement = flatShelves(newData)?.find(
          (updatedItem) => updatedItem.id === oldElement.id,
        );
        return updatedElement && updatedElement.name !== oldElement.name;
      })
      .map((oldElement) =>
        flatShelves(newData)?.find(
          (updatedItem) => updatedItem.id === oldElement.id,
        ),
      ) ?? [];

  // players

  const createdPlayers =
    flatPlayers(newData)?.filter(
      (newItem) =>
        !flatPlayers(oldData)?.some((oldItem) => oldItem.id === newItem.id),
    ) ?? [];

  const deletedPlayers =
    flatPlayers(oldData)?.filter(
      (oldItem) =>
        !flatPlayers(newData)?.some((newtem) => newtem.id === oldItem.id),
    ) ?? [];

  const updatedPlayers =
    flatPlayers(oldData)
      ?.filter((oldElement) => {
        const updatedElement = flatPlayers(newData)?.find(
          (updatedItem) => updatedItem.id === oldElement.id,
        );
        return (
          updatedElement && updatedElement.hostname !== oldElement.hostname
        );
      })
      .map((oldElement) =>
        flatPlayers(newData)?.find(
          (updatedItem) => updatedItem.id === oldElement.id,
        ),
      ) ?? [];

  return {
    createdSubsidiaries,
    deletedSubsidiaries,
    updatedSubsidiaries,
    createdShelves,
    deletedShelves,
    updatedShelves,
    createdPlayers,
    deletedPlayers,
    updatedPlayers,
  };
};

export const useUpdateConfigs = (
  selectedCustomerId: string | undefined,
  oldData: ConfigMap,
  newData: ConfigMap,
) => {
  const { user } = useCurrentAuthSession();
  const { data: userInfo } = useUserInfo();
  const toast = useToast();
  const intl = useIntl();
  const { id: unitId } = useParams();
  const { refetch } = useUnitData(unitId);
  const { refetch: refetchData } = useConfigurations(selectedCustomerId);

  const {
    mutateAsync: updateConfigs,
    isPending: isLoading,
    error,
  } = useMutation({
    mutationFn: async () => {
      if (!oldData || !newData) {
        toast({
          title: intl.formatMessage({
            defaultMessage:
              'Something went wrong updating the Admin Configurations.',
            id: '1jvCGc',
          }),
          mode: TOASTTYPE.Danger,
        });
        return undefined;
      }

      const {
        createdSubsidiaries,
        deletedSubsidiaries,
        updatedSubsidiaries,
        createdShelves,
        deletedShelves,
        updatedShelves,
        createdPlayers,
        deletedPlayers,
        updatedPlayers,
      } = getDiffConfigs(oldData, newData);

      if (
        !createdSubsidiaries?.length &&
        !deletedSubsidiaries?.length &&
        !updatedSubsidiaries?.length &&
        !createdShelves?.length &&
        !deletedShelves?.length &&
        !updatedShelves?.length &&
        !createdPlayers?.length &&
        !deletedPlayers?.length &&
        !updatedPlayers?.length
      ) {
        toast({
          title: intl.formatMessage({
            defaultMessage: 'No changes have been made',
            id: 'XS7f2L',
          }),
          subtitle: intl.formatMessage({
            defaultMessage: 'The Admin configuration was not changed.',
            id: 'A5B2/m',
          }),
          mode: TOASTTYPE.Info,
        });
        return undefined;
      }

      // (1) created subsidiaries

      for (const subsidiary of createdSubsidiaries) {
        try {
          const createSubsidiary = {
            id: selectedCustomerId,
            name: subsidiary?.name,
            subsidiaryId: subsidiary?.id,
            shelves: [],
          };

          await fetcher(`${RETAIL_API_URL}/customers/add/subsidiary`, {
            method: 'POST',
            body: JSON.stringify(createSubsidiary),
          });
          // (1.1) updated news subsidiaries (unit update)

          const refetchedData = await refetch();

          const updateUnit = {
            ...refetchedData.data,
            subsidiaries: [
              createSubsidiary.subsidiaryId,
              ...(refetchedData?.data?.subsidiaries &&
              Array.isArray(refetchedData?.data?.subsidiaries)
                ? // eslint-disable-next-line no-unsafe-optional-chaining
                  refetchedData?.data?.subsidiaries
                : []),
            ],
          };

          await fetcher(`${RETAIL_API_URL}/units/update`, {
            method: 'POST',
            body: JSON.stringify(updateUnit),
          });

          logger.info({
            action: LogAction.CREATE,
            status: LogStatus.SUCCEEDED,
            user: user?.uid ?? 'unkown-user',
            customer: userInfo?.customer ?? 'unkown-customer',
            unit_id: unitId,
            data: {
              created_subsidiary_id: createSubsidiary.id,
            },
          });
        } catch {
          toast({
            title: intl.formatMessage({
              defaultMessage: 'Something went wrong creating the Subsidiary.',
              id: '+ashaL',
            }),
            mode: TOASTTYPE.Danger,
          });
        }
      }

      // (2) delete subsidiaries

      for (const subsidiary of deletedSubsidiaries) {
        try {
          const deleteSubsidiary = {
            id: selectedCustomerId,
            name: subsidiary?.name,
            subsidiaryId: subsidiary.id,
            shelves: subsidiary?.shelves.map((shelf) => shelf.id),
          };

          await fetcher(`${RETAIL_API_URL}/customers/delete/subsidiary`, {
            method: 'POST',
            body: JSON.stringify(deleteSubsidiary),
          });
          // (2.1) updated deleted subsidiaries (unit update)

          const refetchedData = await refetch();

          const updateUnit = {
            ...refetchedData.data,
            subsidiaries: [
              ...(refetchedData?.data?.subsidiaries &&
              Array?.isArray(refetchedData?.data?.subsidiaries)
                ? // eslint-disable-next-line no-unsafe-optional-chaining
                  refetchedData?.data?.subsidiaries
                : []
              ).filter(
                (subsidiaryRefreshed) =>
                  subsidiaryRefreshed !== deleteSubsidiary.subsidiaryId,
              ),
            ],
          };

          await fetcher(`${RETAIL_API_URL}/units/update`, {
            method: 'POST',
            body: JSON.stringify(updateUnit),
          });

          logger.info({
            action: LogAction.DELETE,
            status: LogStatus.SUCCEEDED,
            user: user?.uid ?? 'unkown-user',
            customer: userInfo?.customer ?? 'unkown-customer',
            unit_id: unitId,
            data: {
              deleted_subsidiary_id: deleteSubsidiary.id,
              deleted_subsidiary_name: deleteSubsidiary.name,
            },
          });
        } catch {
          toast({
            title: intl.formatMessage({
              defaultMessage:
                'Something went wrong with deleting the Subsidiary.',
              id: 'byoF2g',
            }),
            mode: TOASTTYPE.Danger,
          });
        }
      }

      // (3) update subsidiaries

      for (const subsidiary of updatedSubsidiaries) {
        try {
          const updateSubsidiary = {
            id: selectedCustomerId,
            name: subsidiary?.name,
            subsidiaryId: subsidiary?.id,
            shelves: subsidiary?.shelves.map(
              (shelfFounded) => shelfFounded?.id ?? shelfFounded,
            ),
          };

          await fetcher(`${RETAIL_API_URL}/customers/update/subsidiary`, {
            method: 'POST',
            body: JSON.stringify(updateSubsidiary),
          });

          logger.info({
            action: LogAction.DELETE,
            status: LogStatus.SUCCEEDED,
            user: user?.uid ?? 'unkown-user',
            customer: userInfo?.customer ?? 'unkown-customer',
            unit_id: unitId,
            data: {
              updated_subsidiary_id: updateSubsidiary.id,
              updated_subsidiary_name: updateSubsidiary.name,
            },
          });
        } catch {
          toast({
            title: intl.formatMessage({
              defaultMessage:
                'Something went wrong changing the name of the Subsidiary.',
              id: 'qA9nB2',
            }),
            mode: TOASTTYPE.Danger,
          });
        }
      }

      // (1) create shelves

      for (const shelf of createdShelves) {
        try {
          const createShelf = {
            id: shelf.id,
            customer: selectedCustomerId,
            name: shelf.name,
            archived: false,
            sensorBoxes: [],
            subsidiary: shelf.subsidiary,
            unitId,
          };

          await fetcher<ConfigMap>(`${RETAIL_API_URL}/shelves/create`, {
            method: 'POST',
            body: JSON.stringify(createShelf),
          });

          const dataRefresh = await refetchData();

          const subsidiaryRefresh = [
            ...(dataRefresh?.data
              ?.filter((item) => item.id === unitId)
              ?.map((item) =>
                item.subsidiaries.map((subsidiary) => ({
                  companyName: item.name,
                  ...subsidiary,
                  shelves: subsidiary.shelves
                    ?.filter((shelfRefreshed) => !shelfRefreshed?.archived)
                    .map((shelfRefreshed) => ({
                      ...shelfRefreshed,
                      players: shelfRefreshed.players?.filter(
                        (player) => !player?.archived,
                      ),
                    })),
                  unitId: item.id,
                })),
              )
              ?.flat() || []),
          ];

          const findSubsidiary = subsidiaryRefresh.find(
            (subsidiary) => subsidiary.id === shelf.subsidiary,
          );

          const updatedSubsidiary = {
            id: selectedCustomerId,
            name: findSubsidiary?.name,
            subsidiaryId: findSubsidiary?.id,
            shelves: findSubsidiary?.shelves.map(
              (shelfFounded) => shelfFounded?.id ?? shelfFounded,
            ),
          };

          await fetcher<ConfigMap>(
            `${RETAIL_API_URL}/customers/update/subsidiary`,
            {
              method: 'POST',
              body: JSON.stringify({
                ...updatedSubsidiary,
                shelves: [...(updatedSubsidiary?.shelves ?? []), shelf.id],
              }),
            },
          );

          // (1.1) updated news shelves (unit update)

          const refetchedData = await refetch();

          const updateUnit = {
            ...refetchedData.data,
            shelves: [
              createShelf.id,
              ...(refetchedData?.data?.shelves &&
              Array?.isArray(refetchedData?.data?.shelves)
                ? // eslint-disable-next-line no-unsafe-optional-chaining
                  refetchedData?.data?.shelves
                : []),
            ],
          };

          await fetcher(`${RETAIL_API_URL}/units/update`, {
            method: 'POST',
            body: JSON.stringify(updateUnit),
          });

          logger.info({
            action: LogAction.CREATE,
            status: LogStatus.SUCCEEDED,
            user: user?.uid ?? 'unkown-user',
            customer: userInfo?.customer ?? 'unkown-customer',
            unit_id: unitId,
            data: {
              created_shelf_id: createShelf.id,
              updated_subsidiary_id: updatedSubsidiary.id,
            },
          });
        } catch {
          toast({
            title: intl.formatMessage({
              defaultMessage:
                'Something went wrong creating the shelf/shelves.',
              id: 'bI4Pj0',
            }),
            mode: TOASTTYPE.Danger,
          });
        }
      }

      // (2) deleted shelves

      for (const shelf of deletedShelves) {
        try {
          const deletedShelf = {
            id: shelf?.id,
          };

          await fetcher(`${RETAIL_API_URL}/shelves/delete`, {
            method: 'POST',
            body: JSON.stringify(deletedShelf),
          });

          const dataRefresh = await refetchData();

          const sortedRefresh = [
            ...(dataRefresh?.data
              ?.filter((item) => item.id === unitId)
              ?.map((item) =>
                item.subsidiaries.map((subsidiary) => ({
                  companyName: item.name,
                  ...subsidiary,
                  shelves: subsidiary.shelves
                    ?.filter((shelfRefreshed) => !shelfRefreshed?.archived)
                    .map((shelfRefreshed) => ({
                      ...shelfRefreshed,
                      players: shelfRefreshed.players?.filter(
                        (player) => !player?.archived,
                      ),
                    })),
                  unitId: item.id,
                })),
              )
              ?.flat() || []),
          ];

          const findSubsidiary = sortedRefresh.find(
            (subsidiary) => subsidiary.id === shelf.subsidiary,
          );

          const updatedSubsidiary = {
            id: selectedCustomerId,
            name: findSubsidiary?.name,
            subsidiaryId: findSubsidiary?.id,
            shelves: findSubsidiary?.shelves.map(
              (shelfFounded) => shelfFounded?.id ?? shelfFounded,
            ),
          };

          await fetcher<ConfigMap>(
            `${RETAIL_API_URL}/customers/update/subsidiary`,
            {
              method: 'POST',
              body: JSON.stringify({
                ...updatedSubsidiary,
                shelves: [...(updatedSubsidiary?.shelves ?? [])].filter(
                  (shelfRefreshed) => shelfRefreshed !== shelf.id,
                ),
              }),
            },
          );

          // (2.1) updated deleted shelves (unit update)

          const refetchedData = await refetch();

          const updateUnit = {
            ...refetchedData.data,
            shelves: [
              ...(refetchedData?.data?.shelves &&
              Array?.isArray(refetchedData?.data?.shelves)
                ? // eslint-disable-next-line no-unsafe-optional-chaining
                  refetchedData?.data?.shelves
                : []
              ).filter((shelfRefreshed) => shelfRefreshed !== shelf.id),
            ],
          };

          await fetcher(`${RETAIL_API_URL}/units/update`, {
            method: 'POST',
            body: JSON.stringify(updateUnit),
          });

          logger.info({
            action: LogAction.DELETE,
            status: LogStatus.SUCCEEDED,
            user: user?.uid ?? 'unkown-user',
            customer: userInfo?.customer ?? 'unkown-customer',
            unit_id: unitId,
            data: {
              deleted_shelf_id: deletedShelf.id,
              updated_subsidiary_id: updatedSubsidiary.id,
            },
          });
        } catch {
          toast({
            title: intl.formatMessage({
              defaultMessage:
                'Something went wrong deleting the shelf/shelves.',
              id: 'QLf2YF',
            }),
            mode: TOASTTYPE.Danger,
          });
        }
      }

      // (3) updated shelves

      for (const shelf of updatedShelves) {
        try {
          const updateShelf = {
            id: shelf?.id,
            customer: selectedCustomerId,
            name: shelf?.name,
            archived: false,
            sensorBoxes: shelf?.sensorBoxes,
            subsidiary: shelf?.subsidiaryId,
            unitId: shelf?.unitId,
          };

          await fetcher<ConfigMap>(`${RETAIL_API_URL}/shelves/update`, {
            method: 'POST',
            body: JSON.stringify(updateShelf),
          });

          logger.info({
            action: LogAction.UPDATE,
            status: LogStatus.SUCCEEDED,
            user: user?.uid ?? 'unkown-user',
            customer: userInfo?.customer ?? 'unkown-customer',
            unit_id: unitId,
            data: {
              updated_shelf_id: updateShelf.id,
            },
          });
        } catch {
          toast({
            title: intl.formatMessage({
              defaultMessage:
                'Something went wrong changing the name(s) of the shelf/shelves',
              id: '3lNhlv',
            }),
            mode: TOASTTYPE.Danger,
          });
        }
      }

      let playerList: readonly CreatedPlayersList[] = [];
      for (const player of createdPlayers) {
        try {
          const createPlayer = {
            hostname: player?.hostname,
            customer: selectedCustomerId,
            shelfId: player?.shelfId,
            subsidiary: player?.subsidiaryId,
            isDevManaged: player?.isDevManaged,
          };

          const responseJson = await fetcher<
            CreatedPlayersList & {
              readonly statusCode?: number;
              readonly reason?: string;
            }
          >(`${RETAIL_API_URL}/players/create`, {
            method: 'POST',
            body: JSON.stringify(createPlayer),
          });
          if (responseJson.statusCode) {
            throw responseJson.reason;
          }
          playerList = [
            ...playerList,
            { ...responseJson, hostname: player?.hostname },
          ];

          logger.info({
            action: LogAction.UPDATE,
            status: LogStatus.SUCCEEDED,
            user: user?.uid ?? 'unkown-user',
            customer: userInfo?.customer ?? 'unkown-customer',
            unit_id: unitId,
            data: {
              created_player_hostname: player?.hostname,
              created_player_customer: createPlayer.customer,
              created_player_shelf_id: createPlayer.shelfId,
              created_player_subsidiary_id: createPlayer.shelfId,
            },
          });
        } catch (err) {
          toast({
            title:
              typeof err === 'string' && err
                ? err
                : intl.formatMessage({
                    defaultMessage:
                      'Something went wrong creating the Players.',
                    id: 'zdAzVF',
                  }),
            mode: TOASTTYPE.Danger,
          });
        }
      }

      // (2) updated players

      for (const player of updatedPlayers) {
        try {
          const updatePlayer = {
            id: player?.id,
            deviceUuid: player?.deviceUuid,
            hostname: player?.hostname,
            fleet: 'g_lukas_knutti/test_sammy',
            customer: selectedCustomerId,
            archived: false,
            shelfId: player?.shelfId,
            subsidiary: player?.subsidiaryId,
          };

          await fetcher(`${RETAIL_API_URL}/players/update`, {
            method: 'POST',
            body: JSON.stringify(updatePlayer),
          });

          logger.info({
            action: LogAction.UPDATE,
            status: LogStatus.SUCCEEDED,
            user: user?.uid ?? 'unkown-user',
            customer: userInfo?.customer ?? 'unkown-customer',
            unit_id: unitId,
            data: {
              updated_player_id: player?.id,
              updated_player_hostname: player?.hostname,
              updated_player_customer: selectedCustomerId,
              updated_player_shelf_id: player?.shelfId,
              updated_player_subsidiary_id: player?.subsidiaryId,
            },
          });
        } catch {
          toast({
            title: intl.formatMessage({
              defaultMessage: 'Something went wrong updating the Players.',
              id: 'mle7Kp',
            }),
            mode: TOASTTYPE.Danger,
          });
        }
      }

      // (3) deleted players

      for (const player of deletedPlayers) {
        try {
          const deletedPlayer = {
            id: player.id,
          };

          await fetcher(`${RETAIL_API_URL}/players/delete`, {
            method: 'POST',
            body: JSON.stringify(deletedPlayer),
          });

          logger.info({
            action: LogAction.UPDATE,
            status: LogStatus.SUCCEEDED,
            user: user?.uid ?? 'unkown-user',
            customer: userInfo?.customer ?? 'unkown-customer',
            unit_id: unitId,
            data: {
              deleted_player_id: player?.id,
              deleted_player_hostname: player?.hostname,
              deleted_player_customer: selectedCustomerId,
              deleted_player_shelf_id: player?.shelfId,
              deleted_player_subsidiary_id: player.subsidiaryId,
            },
          });
        } catch {
          toast({
            title: intl.formatMessage({
              defaultMessage: 'Something went wrong deleting the Player(s)',
              id: 'qdjeWe',
            }),
            mode: TOASTTYPE.Danger,
          });
        }
      }

      toast({
        subtitle: intl.formatMessage({
          defaultMessage: 'Your changes have been saved!',
          id: 'BNq1iK',
        }),
        mode: TOASTTYPE.Success,
      });

      return playerList;
    },
    onError: () => {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Error updating admin configurations',
          id: 'pvTxWM',
        }),
        mode: TOASTTYPE.Danger,
      });
    },
  });

  return {
    updateConfigs,
    isLoading,
    error,
  };
};
